import * as React from "react";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useGenerateColumnAnalysisPromptMutation } from "@/modules/snapshots/businessReports.api";
import { StreamingArtefactMarkdown } from "@/components/feature/markdown/StreamingArtefactMarkdown";
import { TypingIndicator } from "@/components/chat/TypingIndicator";
import { ScrollableChat } from "@/components/feature/chat/ScrollableChat";
import { BarChart3 } from "lucide-react";
import { useColumnAnalysisStream } from "./hooks/useColumnAnalysisStream";
import { useArtefactExtraction } from "@/hooks/useArtefactExtraction";

interface Props {
  businessReportId: string;
  snapshotCollectionId: string;
  subcollectionId?: string;
  fieldName: string;
  onPromptGenerated: (prompt: string) => void;
  contractFamilyProjection?: boolean;
}

export const GeneratePrompt: React.FC<Props> = ({
  businessReportId,
  snapshotCollectionId,
  subcollectionId,
  fieldName,
  onPromptGenerated,
  contractFamilyProjection = false,
}) => {
  const [conversationId, setConversationId] = React.useState<string | null>(null);
  const [generatePrompt] = useGenerateColumnAnalysisPromptMutation();

  const { content, isStreaming } = useColumnAnalysisStream({
    conversationId,
    onComplete: onPromptGenerated,
  });

  const [processedContent, inlineArtefacts] = useArtefactExtraction(content);

  const handleGenerate = async () => {
    const newConversationId = crypto.randomUUID();
    setConversationId(newConversationId);

    try {
      await generatePrompt({
        business_report_id: businessReportId,
        snapshot_collection_id: snapshotCollectionId,
        subcollection_id: subcollectionId ?? null,
        field_name: fieldName,
        conversation_id: newConversationId,
        contract_family_projection: contractFamilyProjection,
      }).unwrap();
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to generate analysis prompt. Please try again.");
      setConversationId(null);
    }
  };

  return (
    <div className="flex h-full flex-col">
      {!conversationId && (
        <div className="flex flex-col items-center justify-center gap-4 p-8">
          <BarChart3 className="h-12 w-12 text-gray-400" />
          <p className="text-center text-sm text-gray-600">
            Generate an AI-powered analysis prompt for the column &quot;{fieldName}&quot;
          </p>
          <Button onClick={handleGenerate}>Generate Analysis Prompt</Button>
        </div>
      )}

      {conversationId && (
        <div className="min-h-0 flex-1">
          <ScrollableChat className="p-4">
            <div className="rounded-lg border border-desert-sand-400 bg-ivory-mist-100 px-3 py-2 text-sm leading-relaxed text-foreground">
              <StreamingArtefactMarkdown content={processedContent} artefacts={inlineArtefacts} />
              <AnimatePresence>
                {isStreaming && (
                  <div className="text-coral">
                    <TypingIndicator />
                  </div>
                )}
              </AnimatePresence>
            </div>
          </ScrollableChat>
        </div>
      )}
    </div>
  );
};
